import React, { useCallback, useEffect, useState } from 'react';
import { Table, Tag, DatePicker, Select, Input, Button, Drawer, Space, Typography, App } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../context/AuthContext';
import { yookassaPaymentsApi } from '../api/yookassa-payments';
import { YookassaPayment, YookassaPaymentDetail, YookassaPaymentStatus } from '../api/types';

const { RangePicker } = DatePicker;
const { Title, Paragraph, Text } = Typography;

const STATUS_COLOR: Record<YookassaPaymentStatus, string> = {
  Pending: 'processing',
  WaitingForCapture: 'warning',
  Succeeded: 'success',
  Canceled: 'error',
  Refunded: 'default',
};

const STATUS_OPTIONS: YookassaPaymentStatus[] = [
  'Pending', 'WaitingForCapture', 'Succeeded', 'Canceled', 'Refunded',
];

const YookassaPaymentsPage: React.FC = () => {
  const { apiClient } = useAuth();
  const { message } = App.useApp();
  const api = useCallback(() => yookassaPaymentsApi(apiClient!), [apiClient]);

  const [items, setItems] = useState<YookassaPayment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);

  const [statuses, setStatuses] = useState<YookassaPaymentStatus[]>([]);
  const [kind, setKind] = useState<string | undefined>();
  const [emailSearch, setEmailSearch] = useState('');
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const [detail, setDetail] = useState<YookassaPaymentDetail | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api().getList({
        page, pageSize,
        statuses: statuses.length > 0 ? statuses : undefined,
        kind: kind ? (kind as 'Module' | 'Bundle') : undefined,
        fromDate: dateRange?.[0]?.toISOString(),
        toDate: dateRange?.[1]?.toISOString(),
        userEmailSearch: emailSearch || undefined,
      });
      setItems(res.data.items);
      setTotal(res.data.totalCount);
    } catch {
      message.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [api, page, pageSize, statuses, kind, dateRange, emailSearch, message]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDetail = async (id: number) => {
    try {
      const res = await api().getById(id);
      setDetail(res.data);
      setDrawerOpen(true);
    } catch {
      message.error('Failed to load payment');
    }
  };

  return (
    <div>
      <Title level={3}>Payments</Title>

      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }} wrap>
        <Select
          mode="multiple"
          placeholder="Status"
          value={statuses}
          onChange={(v) => { setStatuses(v); setPage(1); }}
          style={{ minWidth: 260 }}
          options={STATUS_OPTIONS.map((s) => ({ label: s, value: s }))}
          allowClear
        />
        <Select
          placeholder="Kind"
          value={kind}
          onChange={(v) => { setKind(v); setPage(1); }}
          style={{ minWidth: 140 }}
          options={[{ label: 'Module', value: 'Module' }, { label: 'Bundle', value: 'Bundle' }]}
          allowClear
        />
        <RangePicker value={dateRange as any} onChange={(v) => { setDateRange(v as any); setPage(1); }} />
        <Input.Search
          placeholder="Email starts with..."
          onSearch={(v) => { setEmailSearch(v); setPage(1); }}
          style={{ width: 240 }}
          allowClear
        />
        <Button onClick={fetchData}>Refresh</Button>
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={{
          current: page, pageSize, total,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onRow={(r) => ({ onClick: () => openDetail(r.id), style: { cursor: 'pointer' } })}
        columns={[
          { title: 'Id', dataIndex: 'id', width: 80 },
          { title: 'Created', dataIndex: 'createdAt', render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
          { title: 'User', dataIndex: 'userEmail' },
          { title: 'Kind', dataIndex: 'kind', width: 90 },
          { title: 'Target', dataIndex: 'targetTitle' },
          { title: 'Amount', dataIndex: 'amountRub', width: 110, render: (v: number) => `${v} ₽` },
          { title: 'Status', dataIndex: 'status', width: 130,
            render: (s: YookassaPaymentStatus) => <Tag color={STATUS_COLOR[s]}>{s}</Tag> },
          { title: 'Test', dataIndex: 'isTest', width: 60, render: (v: boolean) => v ? '✓' : '' },
          { title: 'Paid At', dataIndex: 'paidAt', render: (v: string | null) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
          { title: 'External Id', dataIndex: 'externalId', width: 220, render: (v: string | null) => v || '' },
          { title: 'Method', dataIndex: 'paymentMethodType', width: 120, render: (v: string | null) => v || '' },
        ]}
      />

      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={680}
        title={detail ? `Payment #${detail.id}` : 'Payment'}
      >
        {detail && (
          <div>
            <Paragraph><b>Public Id:</b> <Text copyable>{detail.publicId}</Text></Paragraph>
            <Paragraph><b>User:</b> {detail.userEmail}</Paragraph>
            <Paragraph><b>Status:</b> <Tag color={STATUS_COLOR[detail.status]}>{detail.status}</Tag></Paragraph>
            <Paragraph><b>Amount:</b> {detail.amountRub} ₽ (original {detail.originalAmountRub} ₽)</Paragraph>
            <Paragraph><b>Kind:</b> {detail.kind} — {detail.targetTitle}</Paragraph>
            <Paragraph><b>Created:</b> {dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm:ss')}</Paragraph>
            {detail.paidAt && <Paragraph><b>Paid At:</b> {dayjs(detail.paidAt).format('YYYY-MM-DD HH:mm:ss')}</Paragraph>}
            {detail.canceledAt && <Paragraph><b>Canceled At:</b> {dayjs(detail.canceledAt).format('YYYY-MM-DD HH:mm:ss')}</Paragraph>}
            {detail.failureReason && <Paragraph><b>Failure:</b> {detail.failureReason}</Paragraph>}
            {detail.externalId && <Paragraph><b>External Id:</b> {detail.externalId}</Paragraph>}
            {detail.paymentMethodType && <Paragraph><b>Method:</b> {detail.paymentMethodType}</Paragraph>}
            {detail.idempotenceKey && <Paragraph><b>Idempotence Key:</b> {detail.idempotenceKey}</Paragraph>}
            <Paragraph><b>Test:</b> {detail.isTest ? 'Yes' : 'No'}</Paragraph>

            <Title level={5}>Items</Title>
            <ul>
              {detail.items.map((i) => (
                <li key={i.moduleId}>
                  {i.moduleTitle ?? `Module ${i.moduleId}`} — {i.priceRubSnapshot} ₽
                  {i.discountPercentSnapshot ? ` (−${i.discountPercentSnapshot}%)` : ''}
                </li>
              ))}
            </ul>

            <Title level={5}>Events</Title>
            <ul style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {detail.events.map((e, idx) => (
                <li key={idx}>
                  {dayjs(e.createdAt).format('HH:mm:ss')} {e.type} — {e.rawPayload.slice(0, 200)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default YookassaPaymentsPage;
